/** A clock a person can see: whole seconds left of a whole, and whether it stands still. */
export interface ClockView {
  left: number;
  total: number;
  /** Stopped while the device changes hands or a page is open over the game. */
  held: boolean;
}

/**
 * A countdown in whole seconds on the session's scheduler: the Card chooser's seconds, and those
 * a Card reveal waits before it goes on by itself. It ticks once a second, so every view shows the
 * same number without a clock of its own, stands still while held, and at zero calls the `onOut`
 * it was started with.
 */
export class Countdown {
  private view: ClockView | null = null;
  private onOut: (() => void) | null = null;
  private cancel: (() => void) | null = null;
  private held = false;

  constructor(
    private readonly schedule: (fn: () => void, ms: number) => () => void,
    /** Each second's reading. */
    private readonly onTick: (view: ClockView) => void,
  ) {}

  /**
   * Counts `seconds` down from now and returns the first reading; a hold carries over. `total` is
   * the whole they are part of, when some already went by (a person back mid-choice).
   */
  start(seconds: number, onOut: (() => void) | null, total = seconds): ClockView {
    this.stopTimer();
    this.onOut = onOut;
    this.view = { left: seconds, total, held: this.held };
    this.arm();
    return this.view;
  }

  /** Stops or restarts the count; the new reading, or null when nothing counts. */
  hold(held: boolean): ClockView | null {
    if (this.held === held) return null;
    this.held = held;
    if (!this.view) return null;
    this.stopTimer();
    this.view = { ...this.view, held };
    this.arm();
    return this.view;
  }

  stop(): void {
    this.stopTimer();
    this.view = null;
    this.onOut = null;
  }

  private arm(): void {
    const view = this.view;
    if (this.held || !view || view.left <= 0) return;
    this.cancel = this.schedule(() => {
      this.cancel = null;
      const next = { ...view, left: view.left - 1 };
      this.view = next;
      this.onTick(next);
      if (next.left > 0) {
        this.arm();
        return;
      }
      const out = this.onOut;
      this.onOut = null;
      out?.();
    }, 1000);
  }

  private stopTimer(): void {
    this.cancel?.();
    this.cancel = null;
  }
}
